import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Users, Calendar, Sparkles, HandHeart, Loader2, Heart, Lock, Award, Flame, Star, MessageCircle, ShieldCheck } from 'lucide-react';
import Header from '../components/Header';
import TopBars from '../components/TopBars';
import Footer from '../components/Footer';
import AuthModal from '../components/AuthModal';
import CreateCagnotteWizard from '../components/CreateCagnotteWizard';
import ShareCard from '../components/ShareCard';
import DonationModal from '../components/DonationModal';
import { CommentsSection } from '../components/CommentsSection';
import { WithdrawCard } from '../components/WithdrawCard';
import { PrivateDonationsCard } from '../components/PrivateDonationsCard';
import { KycUploadCard } from '../components/KycUploadCard';
import { Confetti } from '../components/Confetti';
import { Button } from '../components/ui/button';
import { api } from '../context/AuthContext';

const formatN = (n) => new Intl.NumberFormat('fr-FR').format(n || 0);

function daysBetween(iso) {
  if (!iso) return null;
  const end = new Date(iso).getTime();
  const diff = Math.max(0, Math.ceil((end - Date.now()) / (1000 * 60 * 60 * 24)));
  return diff;
}

const BADGE_META = {
  first: { label: 'Premier don', Icon: Award, color: '#5a8268' },
  regular: { label: 'Donateur régulier', Icon: Flame, color: '#d97757' },
  top: { label: 'Top donateur', Icon: Star, color: '#b45309' },
};

function DonorBadge({ type }) {
  const meta = BADGE_META[type];
  if (!meta) return null;
  const { label, Icon, color } = meta;
  return (
    <span
      data-testid={`donor-badge-${type}`}
      title={label}
      className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full shrink-0"
      style={{ background: `${color}1a`, color }}
    >
      <Icon className="w-3 h-3" /> {label}
    </span>
  );
}

function DonationItem({ d, accent }) {
  const name = d.isMine ? 'Vous' : (d.donorName || 'Anonyme');
  return (
    <div className="flex items-start gap-3 py-3 border-b border-black/5 last:border-0" data-testid={`donation-item-${d.id}`}>
      <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 text-white font-semibold text-[13px]" style={{ background: accent }}>
        {(name || 'A').substring(0, 1).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 min-w-0">
            <div className="font-medium text-[14px] text-neutral-900 truncate">{name}</div>
            {(d.badges || []).map((b) => <DonorBadge key={b} type={b} />)}
          </div>
          {d.amountVisible ? (
            <div className="text-[13.5px] font-semibold shrink-0" style={{ color: accent }} data-testid="donation-amount-visible">{formatN(d.amount)} FCFA</div>
          ) : (
            <div className="text-[13.5px] font-semibold text-neutral-400 italic flex items-center gap-1 shrink-0" title="Montant confidentiel" data-testid="donation-amount-masked">
              <Lock className="w-3 h-3" /> •••••
            </div>
          )}
        </div>
        {d.message && <p className="mt-0.5 text-[13px] text-neutral-600 leading-snug">“{d.message}”</p>}
        {d.thanksUrl && (
          <a
            href={d.thanksUrl}
            target="_blank"
            rel="noreferrer"
            data-testid={`donation-thanks-btn-${d.id}`}
            className="mt-1.5 inline-flex items-center gap-1 text-[11.5px] font-medium px-2.5 py-1 rounded-full bg-[#25D366]/10 text-[#128C4A] hover:bg-[#25D366]/20 transition-colors"
          >
            <MessageCircle className="w-3 h-3" /> Remercier via WhatsApp
          </a>
        )}
      </div>
    </div>
  );
}

export default function CagnottePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [cagnotte, setCagnotte] = useState(null);
  const [donations, setDonations] = useState([]);
  const [isOrganizer, setIsOrganizer] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [donateOpen, setDonateOpen] = useState(false);
  const [privRefresh, setPrivRefresh] = useState(0);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardType, setWizardType] = useState('Joyeuse');

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [c, d] = await Promise.all([
          api.get(`/cagnottes/${id}`),
          api.get(`/cagnottes/${id}/donations`, { params: { limit: 50 } }).catch(() => ({ data: { items: [] } })),
        ]);
        if (!alive) return;
        setCagnotte(c.data.cagnotte);
        setDonations(d.data.items || []);
        setIsOrganizer(Boolean(d.data.isOrganizer));
        // Update document title dynamically
        if (c.data.cagnotte) {
          document.title = `${c.data.cagnotte.title} — Fodindin`;
        }
      } catch (e) {
        if (alive) setNotFound(true);
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [id]);

  const openCreate = (type = 'Joyeuse') => { setWizardType(type); setWizardOpen(true); };

  if (loading) {
    return (
      <div>
        <Header onCreate={openCreate} />
        <TopBars />
        <div className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
          <Loader2 className="w-6 h-6 animate-spin inline-block text-neutral-500" />
        </div>
        <Footer />
      </div>
    );
  }

  if (notFound || !cagnotte) {
    return (
      <div>
        <Header onCreate={openCreate} />
        <TopBars />
        <div className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
          <h1 className="text-[26px] font-semibold text-[#1a1d1a]">Cagnotte introuvable</h1>
          <p className="mt-2 text-neutral-600">Cette cagnotte n'existe pas ou a été supprimée.</p>
          <Button onClick={() => navigate('/')} className="mt-6 rounded-full bg-[#1a1d1a] hover:bg-black text-white h-11 px-5">Retour à l'accueil</Button>
        </div>
        <Footer />
      </div>
    );
  }

  const isYako = cagnotte.type === 'Yako';
  const accent = isYako ? '#5a8268' : '#d97757';
  const accentBg = isYako ? 'bg-[#5a8268]' : 'bg-[#d97757]';
  const accentHover = isYako ? 'hover:bg-[#4a6f57]' : 'hover:bg-[#c66745]';
  const pct = Math.min(100, Math.round(((cagnotte.raised || 0) / (cagnotte.goal || 1)) * 100));
  const goalReached = pct >= 100;
  const daysLeft = daysBetween(cagnotte.endsAt);

  return (
    <div>
      {goalReached && <Confetti />}
      <Header onCreate={openCreate} />
      <TopBars />

      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link to="/" className="inline-flex items-center gap-1.5 text-[13px] text-neutral-600 hover:text-black">
          <ArrowLeft className="w-4 h-4" /> Toutes les cagnottes
        </Link>

        <div className="mt-6 grid lg:grid-cols-12 gap-8">
          {/* Left column */}
          <div className="lg:col-span-7 space-y-6">
            <div className="rounded-3xl overflow-hidden border border-black/5 bg-white">
              <div className="aspect-[16/9] bg-neutral-100 relative overflow-hidden">
                <img src={cagnotte.image} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover blur-2xl scale-110 opacity-70" onError={(e) => { e.target.style.display = 'none'; }} />
                <img data-testid="cagnotte-cover-image" src={cagnotte.image} alt={cagnotte.title} draggable={false} onContextMenu={(e) => e.preventDefault()} className="absolute inset-0 w-full h-full object-contain" onError={(e) => { e.target.classList.remove('object-contain'); e.target.classList.add('object-cover'); e.target.src = isYako ? 'https://images.unsplash.com/photo-1655720359248-eeace8c709c5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200' : 'https://images.unsplash.com/photo-1647616927583-1d44a79a38a5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200'; }} />
                <div className="absolute top-4 left-4 flex items-center gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-md text-white flex items-center gap-1" style={{ background: accent }}>
                    {isYako ? <HandHeart className="w-3 h-3" /> : <Sparkles className="w-3 h-3" />} {cagnotte.type}
                  </span>
                  <span className="text-[11px] bg-white/90 text-neutral-700 rounded-md px-2 py-1">{cagnotte.category}</span>
                </div>
              </div>
              <div className="p-6 sm:p-8">
                <div className="flex items-center gap-3 text-[12.5px] text-neutral-500">
                  <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {cagnotte.country}</span>
                  {daysLeft !== null && (
                    <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {daysLeft > 0 ? `J-${daysLeft}` : 'Terminée'}</span>
                  )}
                </div>
                <h1 className="mt-2 text-[clamp(1.6rem,3vw,2.4rem)] font-semibold tracking-tight text-[#1a1d1a] leading-tight">{cagnotte.title}</h1>
                {cagnotte.owner?.name && (
                  <div className="mt-2 text-[13.5px] text-neutral-600">Organisée par <span className="font-medium text-neutral-800">{cagnotte.owner.name}</span></div>
                )}
                {cagnotte.requiresKyc && (
                  <div data-testid="kyc-notice" className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-medium text-[#b45309] bg-[#f59e0b]/10 border border-[#f59e0b]/20 px-3 py-1.5 rounded-full">
                    <ShieldCheck className="w-3.5 h-3.5" /> Cagnotte à objectif élevé — vérification d'identité (KYC) requise avant le retrait des fonds
                  </div>
                )}
                <p className="mt-5 text-[15px] leading-relaxed text-neutral-700 whitespace-pre-line">{cagnotte.description}</p>
              </div>
            </div>

            {/* Donations list */}
            <div className="rounded-3xl bg-white border border-black/5 p-6 sm:p-7">
              <div className="flex items-baseline justify-between">
                <h2 className="text-[18px] font-semibold text-[#1a1d1a]">Derniers dons</h2>
                <span className="text-[12.5px] text-neutral-500">{cagnotte.donors || 0} donateurs</span>
              </div>
              {!isOrganizer && donations.length > 0 && (
                <p className="mt-2 text-[11.5px] text-neutral-500 flex items-center gap-1.5" data-testid="donations-privacy-note">
                  <Lock className="w-3 h-3 shrink-0" /> Montants confidentiels — seuls l'organisateur et chaque donateur (pour son propre don) peuvent les voir.
                </p>
              )}
              {donations.length === 0 ? (
                <div className="mt-6 text-center py-10 text-[13.5px] text-neutral-500">
                  Soyez le premier à soutenir cette cagnotte.
                </div>
              ) : (
                <div className="mt-3">
                  {donations.map((d) => <DonationItem key={d.id} d={d} accent={accent} />)}
                </div>
              )}
            </div>

            {/* Private donations (organizer only) */}
            {isOrganizer && <PrivateDonationsCard cagnotteId={id} accent={accent} refreshKey={privRefresh} />}

            {/* Comments (moderated by organizer) */}
            <CommentsSection cagnotteId={id} accent={accent} />
          </div>

          {/* Right column */}
          <div className="lg:col-span-5 space-y-4 lg:sticky lg:top-24 self-start">
            <div className="rounded-3xl bg-white border border-black/5 p-6">
              {goalReached && (
                <div className="mb-3 inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-wider px-3 py-1 rounded-full text-white animate-pulse" style={{ background: accent }} data-testid="goal-reached-badge">
                  <Sparkles className="w-3.5 h-3.5" /> Objectif atteint !
                </div>
              )}
              <div className="text-[28px] font-semibold text-[#1a1d1a]">{formatN(cagnotte.raised || 0)} <span className="text-[14px] font-medium text-neutral-500">FCFA</span></div>
              <div className="text-[12.5px] text-neutral-500 mt-0.5">collectés sur {formatN(cagnotte.goal)} FCFA</div>
              <div className="mt-4 h-2 rounded-full bg-neutral-200 overflow-hidden">
                <div className="h-full rounded-full transition-[width] duration-1000 ease-out" style={{ width: `${pct}%`, background: accent }} />
              </div>
              <div className="mt-2 flex items-center justify-between text-[12px] text-neutral-500">
                <span>{pct}% de l'objectif</span>
                <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {cagnotte.donors || 0}</span>
              </div>
              <Button data-testid="cagnotte-donate-btn" onClick={() => setDonateOpen(true)} className={`btn-anim mt-5 w-full h-12 rounded-full text-white ${accentBg} ${accentHover} text-[14.5px] font-medium`}>
                <Heart className="w-4 h-4 mr-1.5" /> Faire un don
              </Button>
              {goalReached && isOrganizer && (
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(`🎉 Objectif atteint ! Un immense MERCI à tous les donateurs de « ${cagnotte.title} » — grâce à vous, nous avons collecté ${formatN(cagnotte.raised)} FCFA ! ${window.location.origin}/cagnotte/${cagnotte.id}`)}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="goal-reached-whatsapp-btn"
                  className="btn-anim mt-2.5 w-full h-11 rounded-full bg-[#25D366] hover:bg-[#1fbb59] text-white text-[13.5px] font-medium flex items-center justify-center gap-2 transition-colors"
                >
                  <MessageCircle className="w-4 h-4" /> Remercier tout le monde sur WhatsApp
                </a>
              )}
            </div>

            {isOrganizer && cagnotte.requiresKyc && (
              <KycUploadCard
                cagnotte={cagnotte}
                onSubmitted={() => api.get(`/cagnottes/${id}`).then((r) => setCagnotte(r.data.cagnotte)).catch(() => {})}
              />
            )}

            <WithdrawCard
              cagnotte={cagnotte}
              onWithdrawn={() => {
                api.get(`/cagnottes/${id}`).then((r) => setCagnotte(r.data.cagnotte)).catch(() => {});
              }}
            />

            <ShareCard cagnotte={cagnotte} />
          </div>
        </div>
      </div>

      <Footer />

      <AuthModal />
      <CreateCagnotteWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        initialType={wizardType}
        onCreated={(created) => { if (created?.id) navigate(`/cagnotte/${created.id}`); }}
      />
      <DonationModal
        open={donateOpen}
        onOpenChange={setDonateOpen}
        cagnotte={cagnotte}
        onDonated={(updated) => {
          setCagnotte(updated);
          setPrivRefresh((v) => v + 1);
          // Refresh donations
          api.get(`/cagnottes/${id}/donations`, { params: { limit: 50 } }).then(r => { setDonations(r.data.items || []); setIsOrganizer(Boolean(r.data.isOrganizer)); }).catch(() => {});
        }}
      />
    </div>
  );
}
